import {useCallback, useEffect, useRef, useState} from 'react'
import {useQuery, type UseQueryResult} from '@tanstack/react-query'

import {useFeatureFlag} from 'hooks'
import {AIUsageAppDopamineAPIResponse} from 'interfaces'
import {AIUsageService} from 'services/api'

export const AI_USAGE_APP_DOPAMINE_QUERY_KEY = 'getAIUsageAppDopamine'
export const AI_USAGE_DOPAMINE_POLL_INTERVAL_MS = 5000
// About 2 minutes of polling at the default interval
export const AI_USAGE_DOPAMINE_MAX_POLL_ATTEMPTS = 24

export type UseAIUsageAppDopamineQueryProps = {
  appName: string
  enabled?: boolean
  pollingInterval?: number
  maxPollAttempts?: number
}

type UseAIUsageAppDopamineQueryResult = Pick<
  UseQueryResult<AIUsageAppDopamineAPIResponse, string>,
  'data' | 'isLoading' | 'isError' | 'error' | 'refetch'
> & {
  isPending: boolean
  isReady: boolean
  hasPollingTimedOut: boolean
}

export const useAIUsageAppDopamineQuery = ({
  appName,
  enabled = true,
  pollingInterval = AI_USAGE_DOPAMINE_POLL_INTERVAL_MS,
  maxPollAttempts = AI_USAGE_DOPAMINE_MAX_POLL_ATTEMPTS,
}: UseAIUsageAppDopamineQueryProps): UseAIUsageAppDopamineQueryResult => {
  const {isAIUsageAppsDopamineEnabled} = useFeatureFlag()
  const attemptsRef = useRef(0)
  const [attempts, setAttempts] = useState(0)

  useEffect(() => {
    attemptsRef.current = 0
    setAttempts(0)
  }, [appName])

  const {
    data,
    isLoading,
    fetchStatus,
    isError,
    error,
    refetch: refetchQuery,
  } = useQuery<AIUsageAppDopamineAPIResponse, string>({
    queryKey: [AI_USAGE_APP_DOPAMINE_QUERY_KEY, appName],
    queryFn: async () => {
      attemptsRef.current += 1
      setAttempts(attemptsRef.current)
      return AIUsageService.getAIUsageAppDopamine(appName)
    },
    enabled: Boolean(isAIUsageAppsDopamineEnabled && enabled && appName),
    retry: false,
    refetchOnWindowFocus: false,
    refetchInterval: (queryData, query) =>
      queryData?.status === 'pending' &&
      query.state.status !== 'error' &&
      attemptsRef.current < maxPollAttempts
        ? pollingInterval
        : false,
  })

  const refetch = useCallback<typeof refetchQuery>(
    options => {
      attemptsRef.current = 0
      setAttempts(0)
      return refetchQuery(options)
    },
    [refetchQuery],
  )

  const isPending = data?.status === 'pending'

  return {
    data,
    // Disabled queries report isLoading in React Query v4
    isLoading: isLoading && fetchStatus !== 'idle',
    isError,
    error,
    refetch,
    isPending,
    isReady: data?.status === 'ready',
    hasPollingTimedOut: isPending && attempts >= maxPollAttempts,
  }
}
